import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, TemplateRef, OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';

export type PopoverPosition = 'top' | 'bottom' | 'left' | 'right';
export type PopoverTrigger = 'click' | 'hover' | 'manual';

@Component({
  selector: 'app-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss']
})
export class PopoverComponent implements OnInit, OnDestroy {
  @ViewChild('trigger') triggerElement!: ElementRef;
  @ViewChild('popoverContent') popoverContent!: ElementRef;

  @Input() position: PopoverPosition = 'bottom';
  @Input() trigger: PopoverTrigger = 'click';
  @Input() visible: boolean = false;
  @Input() closeOnClickOutside: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() header: string = '';
  @Input() footer: string = '';
  @Input() contentTemplate!: TemplateRef<any>;
  @Input() portal: boolean = false;
  @Input() width: string = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() show = new EventEmitter<void>();
  @Output() hide = new EventEmitter<void>();

  isVisible: boolean = false;
  effectivePosition: PopoverPosition = 'bottom';
  popoverStyle: any = {};
  hoverTimeout: any;
  private repositionTimeout: any;
  private boundClickHandler: any;
  private boundKeydownHandler: any;

  constructor(private ngZone: NgZone) {
    this.boundClickHandler = (event: MouseEvent) => this.onDocumentClick(event);
    this.boundKeydownHandler = (event: KeyboardEvent) => this.onDocumentKeydown(event);
  }

  ngOnInit(): void {
    this.isVisible = this.visible;
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('click', this.boundClickHandler);
      document.addEventListener('keydown', this.boundKeydownHandler);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.boundClickHandler);
    document.removeEventListener('keydown', this.boundKeydownHandler);
    clearTimeout(this.repositionTimeout);
    clearTimeout(this.hoverTimeout);
  }

  private onDocumentClick(event: MouseEvent): void {
    if (this.closeOnClickOutside && this.isVisible) {
      const target = event.target as HTMLElement;
      const triggerContains = this.triggerElement && this.triggerElement.nativeElement.contains(target);
      const popoverContains = this.popoverContent && this.popoverContent.nativeElement.contains(target);
      if (!triggerContains && !popoverContains) {
        this.ngZone.run(() => this.close());
      }
    }
  }

  private onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.closeOnEscape && this.isVisible) {
      this.ngZone.run(() => this.close());
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    if (this.closeOnEscape && this.isVisible) {
      this.close();
    }
  }

  onTriggerClick(event: Event): void {
    event.stopPropagation();
    if (this.trigger === 'click') {
      this.toggle();
    }
  }

  onTriggerMouseEnter(): void {
    if (this.trigger === 'hover') {
      this.hoverTimeout = setTimeout(() => {
        this.open();
      }, 200);
    }
  }

  onTriggerMouseLeave(): void {
    if (this.trigger === 'hover') {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = setTimeout(() => {
        this.close();
      }, 100);
    }
  }

  onPopoverMouseLeave(): void {
    if (this.trigger === 'hover') {
      this.hoverTimeout = setTimeout(() => {
        this.close();
      }, 100);
    }
  }

  onPopoverMouseEnter(): void {
    if (this.trigger === 'hover') {
      clearTimeout(this.hoverTimeout);
    }
  }

  onPopoverContentClick(event: Event): void {
    event.stopPropagation();
  }

  toggle(): void {
    if (this.isVisible) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.effectivePosition = this.resolvePosition();
    this.isVisible = true;
    this.visible = true;
    this.visibleChange.emit(true);
    this.show.emit();
    this.updatePosition();
    clearTimeout(this.repositionTimeout);
    this.repositionTimeout = setTimeout(() => {
      if (!this.isVisible) { return; }
      const refined = this.resolvePosition();
      if (refined !== this.effectivePosition) {
        this.effectivePosition = refined;
        this.updatePosition();
      }
    }, 0);
  }

  close(): void {
    this.isVisible = false;
    this.visible = false;
    this.visibleChange.emit(false);
    this.hide.emit();
  }

  private resolvePosition(): PopoverPosition {
    if (!this.triggerElement) { return this.position; }
    const triggerRect = this.triggerElement.nativeElement.getBoundingClientRect();
    const offset = 12;
    const margin = 8;
    let popoverHeight = 150;
    let popoverWidth = 250;
    if (this.popoverContent && this.popoverContent.nativeElement) {
      const h = this.popoverContent.nativeElement.offsetHeight;
      const w = this.popoverContent.nativeElement.offsetWidth;
      if (h > 0) { popoverHeight = h; }
      if (w > 0) { popoverWidth = w; }
    }
    switch (this.position) {
      case 'top':
        if (triggerRect.top - offset - margin < popoverHeight) { return 'bottom'; }
        return 'top';
      case 'bottom':
        if (window.innerHeight - triggerRect.bottom - offset - margin < popoverHeight) { return 'top'; }
        return 'bottom';
      case 'left':
        if (triggerRect.left - offset - margin < popoverWidth) { return 'right'; }
        return 'left';
      case 'right':
        if (window.innerWidth - triggerRect.right - offset - margin < popoverWidth) { return 'left'; }
        return 'right';
      default:
        return this.position;
    }
  }

  private updatePosition(): void {
    if (!this.triggerElement) { return; }

    const triggerRect = this.triggerElement.nativeElement.getBoundingClientRect();
    const offset = 12;

    if (this.portal) {
      switch (this.effectivePosition) {
        case 'top':
          this.popoverStyle = {
            position: 'fixed',
            bottom: (window.innerHeight - triggerRect.top + offset) + 'px',
            left: (triggerRect.left + triggerRect.width / 2) + 'px',
            transform: 'translateX(-50%)'
          };
          break;
        case 'bottom':
          this.popoverStyle = {
            position: 'fixed',
            top: (triggerRect.bottom + offset) + 'px',
            left: (triggerRect.left + triggerRect.width / 2) + 'px',
            transform: 'translateX(-50%)'
          };
          break;
        case 'left':
          this.popoverStyle = {
            position: 'fixed',
            top: (triggerRect.top + triggerRect.height / 2) + 'px',
            right: (window.innerWidth - triggerRect.left + offset) + 'px',
            transform: 'translateY(-50%)'
          };
          break;
        case 'right':
          this.popoverStyle = {
            position: 'fixed',
            top: (triggerRect.top + triggerRect.height / 2) + 'px',
            left: (triggerRect.right + offset) + 'px',
            transform: 'translateY(-50%)'
          };
          break;
      }
      return;
    }

    switch (this.effectivePosition) {
      case 'top':
        this.popoverStyle = {
          bottom: `calc(100% + ${offset}px)`,
          left: '50%',
          transform: 'translateX(-50%)'
        };
        break;
      case 'bottom':
        this.popoverStyle = {
          top: `calc(100% + ${offset}px)`,
          left: '50%',
          transform: 'translateX(-50%)'
        };
        break;
      case 'left':
        this.popoverStyle = {
          right: `calc(100% + ${offset}px)`,
          top: '50%',
          transform: 'translateY(-50%)'
        };
        break;
      case 'right':
        this.popoverStyle = {
          left: `calc(100% + ${offset}px)`,
          top: '50%',
          transform: 'translateY(-50%)'
        };
        break;
    }
  }

  getContentStyle(): any {
    if (this.width) {
      return Object.assign({}, this.popoverStyle, { width: this.width, maxWidth: 'none' });
    }
    return this.popoverStyle;
  }

  getPopoverClasses(): string {
    const classes = ['popover-content', 'position-' + this.effectivePosition];
    if (this.isVisible) { classes.push('visible'); }
    return classes.join(' ');
  }
}
